import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MockScenario } from '@shared/contracts/mockApi';
import { db, resetDatabase, setCompanyStatus, setOffline, setScenario } from '@/mocks/db';
import { resetLayouts } from '@/mocks/layoutApi';
import { advanceMinutes, nowIso, resetClock } from '@/services/clock';
import { useSession } from '@/state/session';
import { bumpDataVersion } from '@/state/dataVersion';

/**
 * Desktop presentation stage.
 *
 * On a phone the app fills the screen. On a laptop a bare 480px column floating
 * on a flat background reads as broken, so from 1000px up the same shell is
 * presented inside a device frame, with product context on one side and live
 * controls on the other.
 *
 * The children render inline — styling, not an iframe — so the controls here
 * act on the *same* running application. (An earlier iframe-based version could
 * not do this: the framed app ran in a separate JS context, so switching a
 * scenario never reached it.)
 */

const PERSONAS = [
  { id: 'user_owner', name: 'Priya Raman', role: 'Business owner', initials: 'PR' },
  { id: 'user_finance', name: 'Daniel Okafor', role: 'Finance', initials: 'DO' },
  { id: 'user_approver', name: 'Helena Vasquez', role: 'Approver', initials: 'HV' },
  { id: 'user_employee', name: 'Tom Whitfield', role: 'Cardholder', initials: 'TW' },
  { id: 'user_readonly', name: 'Grace Lindholm', role: 'Read-only', initials: 'GL' },
] as const;

const SCENARIOS: readonly { value: MockScenario; label: string }[] = [
  { value: 'normal', label: 'Everything working' },
  { value: 'empty', label: 'No data yet' },
  { value: 'slow', label: 'Slow connection' },
  { value: 'provider-unavailable', label: 'Bank unreachable' },
  { value: 'failed-payment', label: 'Payments fail' },
  { value: 'pending-approval', label: 'Everything needs approval' },
  { value: 'ai-unavailable', label: 'Assistant unavailable' },
  { value: 'large-dataset', label: 'Large dataset' },
];

/**
 * The Tribe mark: three ascending bars inside a rounded square, echoing a
 * balance/growth motif without being a literal chart. Used everywhere the
 * product needs a logo — the desktop stage and the login screen — so there is
 * exactly one mark defined, never a text-character stand-in.
 */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Tribe"
    >
      <rect width="40" height="40" rx="11" fill="currentColor" />
      <path
        d="M13 25.5V17M20 25.5V12M27 25.5V20.5"
        stroke="var(--tm-brandmark-fg, #fff)"
        strokeWidth="3.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function DesktopStage({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const currentUserId = useSession((s) => s.userId);
  const [scenario, setScenarioState] = useState<MockScenario>('normal');
  const [offline, setOfflineState] = useState(false);

  /** Switch person without going through the sign-in screen. */
  const signInAs = (userId: string) => {
    const user = db().users.find((u) => u.id === userId);
    const companyId = user?.companyIds[0] ?? 'company_healthy_ltd';
    useSession.setState({
      phase: 'ready',
      userId,
      companyId,
      scaSatisfiedAt: nowIso(),
      startedAt: nowIso(),
      pendingChallengeId: null,
      returnTo: null,
    });
    bumpDataVersion();
    navigate('/mobile/home');
  };

  return (
    <div className="tm-stage">
      <div className="tm-stage__ambient" aria-hidden="true" />

      <aside className="tm-stage__aside tm-stage__aside--left" aria-hidden="true">
        <div className="tm-stage__brand">
          <BrandMark className="tm-stage__mark" />
          <div>
            <p className="tm-stage__brandname">Tribe</p>
            <p className="tm-stage__brandsub">SME Mobile App</p>
          </div>
        </div>
        <p className="tm-stage__tagline">
          Balances, payments, approvals and card controls — built for one hand.
        </p>
        <ul className="tm-stage__points">
          <li>Navigation that follows your role</li>
          <li>Payee verification before every payment</li>
          <li>Extra security on sensitive actions</li>
          <li>A home page you can arrange yourself</li>
        </ul>
      </aside>

      <div className="tm-stage__device">
        <div className="tm-stage__bezel">
          <span className="tm-stage__notch" aria-hidden="true" />
          <div className="tm-stage__screen">{children}</div>
        </div>
      </div>

      {/* Live controls. These act on the running app, not a copy of it. */}
      <aside className="tm-stage__aside tm-stage__aside--right" aria-label="Preview controls">
        <section className="tm-stage__panel">
          <h2 className="tm-stage__paneltitle">Signed in as</h2>
          <div className="tm-stage__personas">
            {PERSONAS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`tm-stage__persona${currentUserId === p.id ? ' is-current' : ''}`}
                aria-pressed={currentUserId === p.id}
                onClick={() => signInAs(p.id)}
              >
                <span className="tm-stage__personaAvatar" aria-hidden="true">
                  {p.initials}
                </span>
                <span>
                  <span className="tm-stage__personaName">{p.name}</span>
                  <span className="tm-stage__personaRole">{p.role}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="tm-stage__panel">
          <label className="tm-stage__paneltitle" htmlFor="stage-scenario">
            Conditions
          </label>
          <select
            id="stage-scenario"
            className="tm-stage__select"
            value={scenario}
            onChange={(e) => {
              const next = e.target.value as MockScenario;
              setScenarioState(next);
              setScenario(next);
              bumpDataVersion();
            }}
          >
            {SCENARIOS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <div className="tm-stage__row">
            <button
              type="button"
              className="tm-stage__chip"
              aria-pressed={offline}
              onClick={() => {
                const next = !offline;
                setOfflineState(next);
                setOffline(next);
                bumpDataVersion();
              }}
            >
              {offline ? 'Back online' : 'Go offline'}
            </button>
            <button
              type="button"
              className="tm-stage__chip"
              onClick={() => {
                setCompanyStatus(
                  'company_healthy_ltd',
                  'restricted',
                  'Outgoing payments are paused while we complete a compliance review.',
                );
                bumpDataVersion();
              }}
            >
              Restrict company
            </button>
            <button
              type="button"
              className="tm-stage__chip"
              onClick={() => {
                advanceMinutes(90);
                bumpDataVersion();
                navigate('/mobile/home');
              }}
            >
              Expire session
            </button>
          </div>
        </section>

        <button
          type="button"
          className="tm-stage__reset"
          onClick={() => {
            resetDatabase();
            resetLayouts();
            resetClock();
            setScenarioState('normal');
            setOfflineState(false);
            useSession.getState().signOut();
            bumpDataVersion();
            navigate('/mobile/login');
          }}
        >
          Reset everything
        </button>
        <p className="tm-stage__fineprint">
          Sample data only. Nothing here moves real money.
        </p>
      </aside>
    </div>
  );
}
